/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps, Theme } from '@mui/material';
import { Grid, LinearProgress, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import type { i18n, TFunction } from 'i18next';
import React from 'react';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { ToolContainerDraggable } from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import { DimensionsTime, DrawRegion } from '@opengeoweb/theme';
import { useTimeseriesTranslation } from '../../../utils/i18n';
import type { EDRInstance, Parameter } from '../../TimeSeries/types';
import type { TimeseriesCollectionDetail } from '../../TimeSeriesSelect/utils';

export interface ParameterInfoDialogProps {
  currentParameterInfo: Parameter;
  relatedService: TimeSeriesService | undefined;
  collectionInformation: TimeseriesCollectionDetail | undefined;
  isLoadingCollectionInformation: boolean;
  instanceInformation: EDRInstance[] | undefined;
  isLoadingInstanceInformation: boolean;
  supportsInstances: boolean;
  onClose: () => void;
  isOpen: boolean;
  onMouseDown?: () => void;
  order?: number;
  source?: uiTypes.Source;
  dialogHeight?: number;
}

export const ParameterInfoDialog: React.FC<ParameterInfoDialogProps> = ({
  onClose,
  currentParameterInfo,
  collectionInformation,
  isLoadingCollectionInformation,
  instanceInformation,
  isLoadingInstanceInformation,
  supportsInstances,
  relatedService,
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  dialogHeight,
}: ParameterInfoDialogProps) => {
  const { t } = useTimeseriesTranslation();

  return (
    <ToolContainerDraggable
      isOpen={isOpen}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={{ top: 150, left: 790 }}
      startSize={{ width: 288, height: dialogHeight ?? 650 }}
      title={t('timeseries-info-title')}
    >
      <Box sx={{ padding: 2 }}>
        {!!currentParameterInfo.propertyLabel && (
          <ParameterInfoText
            label={t('timeseries-info-label')}
            value={currentParameterInfo.propertyLabel}
          />
        )}
        <ParameterInfoText
          label={t('timeseries-info-name')}
          value={currentParameterInfo.propertyName}
        />
        <ParameterInfoText
          label={t('timeseries-info-service')}
          value={relatedService?.name || currentParameterInfo.serviceId}
        />
        {!!relatedService && (
          <>
            <ParameterInfoText
              label={t('timeseries-info-service-url')}
              value={relatedService.url}
            />
            <ParameterInfoText
              label={t('timeseries-info-service-description')}
              value={relatedService.description}
            />
            <ParameterInfoText
              label={t('timeseries-info-service-type')}
              value={relatedService.type}
            />
          </>
        )}
        <ParameterInfoText
          label={t('timeseries-collection-id')}
          value={currentParameterInfo.collectionId}
        />
        {!!collectionInformation?.collectionTitle &&
          collectionInformation?.collectionTitle !== '' && (
            <ParameterInfoText
              label={t('timeseries-collection-title')}
              value={collectionInformation?.collectionTitle}
            />
          )}
        {!!collectionInformation?.collectionDescription &&
          collectionInformation?.collectionDescription !== '' && (
            <ParameterInfoText
              label={t('timeseries-collection-description')}
              value={collectionInformation?.collectionDescription}
            />
          )}

        <QueryInfoElement
          isLoadingCollectionInformation={isLoadingCollectionInformation}
          queryInformation={collectionInformation}
        />
        {supportsInstances ? (
          <DimensionInfoElement
            instanceInformation={instanceInformation}
            isLoading={isLoadingInstanceInformation}
          />
        ) : (
          // If no instances, display temporal and spatial extent here
          collectionInformation && (
            <ParameterInfoList
              key={currentParameterInfo.collectionId}
              label={currentParameterInfo.collectionId}
              list={generateInfoList(collectionInformation, t)}
            />
          )
        )}
      </Box>
    </ToolContainerDraggable>
  );
};

interface LoadingIndicatorProps {
  subtitle: string;
  textStyle: SxProps<Theme>;
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  subtitle,
  textStyle,
}) => {
  return (
    <>
      <Typography sx={textStyle} variant="subtitle2">
        {subtitle}
      </Typography>
      <Box sx={{ position: 'relative', marginBottom: 3 }}>
        <LinearProgress
          color="secondary"
          data-testid="loadingIndicator"
          sx={{ position: 'absolute', width: '100%', top: 8 }}
        />
      </Box>
    </>
  );
};

export const QueryInfoElement: React.FC<{
  queryInformation: TimeseriesCollectionDetail | undefined;
  isLoadingCollectionInformation: boolean;
}> = ({ queryInformation, isLoadingCollectionInformation }) => {
  const { t } = useTimeseriesTranslation();

  if (isLoadingCollectionInformation) {
    return (
      <LoadingIndicator
        subtitle={t('timeseries-info-queries')}
        textStyle={textStyle}
      />
    );
  }

  const queryInfo = !queryInformation
    ? t('timeseries-info-no-data-available')
    : ((): string => {
        const { dataQueries } = queryInformation;

        const queries = [
          dataQueries?.locations && 'locations',
          dataQueries?.position && 'position',
        ].filter(Boolean);
        return queries.length === 0
          ? t('timeseries-info-no-data-available')
          : queries.join(', ');
      })();

  return (
    <>
      <Typography sx={textStyle} variant="subtitle2">
        {t('timeseries-info-queries')}
      </Typography>
      <Typography sx={textStyleWithMargin} variant="body2">
        {queryInfo}
      </Typography>
    </>
  );
};

const generateInfoList = (
  element: EDRInstance | TimeseriesCollectionDetail,
  t: TFunction<'timeseries', i18n>,
): ListItem[] => {
  const infoList: ListItem[] = [];
  if (element.extent?.temporal?.values) {
    infoList.push({
      label: t('timeseries-info-time'),
      icon: <DimensionsTime />,
      value: element.extent.temporal.values.join(', '),
    });
  }
  if (element.extent?.spatial?.bbox) {
    const bbox = element.extent.spatial.bbox[0];
    const value = t('timeseries-info-bbox-rendering', {
      west: bbox[0],
      south: bbox[1],
      east: bbox[2],
      north: bbox[3],
    });
    infoList.push({
      label: t('timeseries-info-bbox'),
      icon: <DrawRegion />,
      value:
        value === 'timeseries-info-bbox-rendering' ? bbox.join(', ') : value,
    });
  }
  return infoList;
};

export const DimensionInfoElement: React.FC<{
  instanceInformation: EDRInstance[] | undefined;
  isLoading: boolean;
}> = ({ instanceInformation, isLoading }) => {
  const { t } = useTimeseriesTranslation();
  if (isLoading) {
    return (
      <LoadingIndicator
        subtitle={t('timeseries-info-instance-dimensions')}
        textStyle={textStyle}
      />
    );
  }
  return (
    <>
      <Typography sx={textStyle} variant="subtitle2">
        {t('timeseries-info-instance-dimensions')}
      </Typography>
      {!instanceInformation || instanceInformation.length === 0 ? (
        <Typography sx={textStyleWithMargin} variant="body2">
          {t('timeseries-info-no-data-available')}
        </Typography>
      ) : (
        // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
        [...instanceInformation]
          .sort()
          .reverse()
          .map((element) => {
            const infoList = generateInfoList(element, t);
            return (
              <ParameterInfoList
                key={element.id}
                label={element.id}
                list={infoList}
              />
            );
          })
      )}
    </>
  );
};

export interface ParameterInfoTextProps {
  label: string;
  value: string;
}

const textStyle = {
  lineHeight: '20px',
  letterSpacing: '0.25px',
};

const textStyleWithMargin = {
  ...textStyle,
  marginBottom: 1,
};

const ParameterInfoText: React.FC<ParameterInfoTextProps> = ({
  label,
  value,
}: ParameterInfoTextProps) => {
  return (
    <>
      <Typography sx={textStyle} variant="subtitle2">
        {label}
      </Typography>
      <Typography sx={textStyleWithMargin} variant="body2">
        {value}
      </Typography>
    </>
  );
};

export interface ListItem {
  label: string;
  value: string;
  icon: React.ReactElement;
}

export interface ParameterInfoListProps {
  label: string;
  list: ListItem[];
}

const valueStyle = {
  ...textStyle,
  overflowWrap: 'break-word',
};

export const ParameterInfoList: React.FC<ParameterInfoListProps> = ({
  label,
  list,
}: ParameterInfoListProps) => {
  return (
    <>
      <Typography sx={textStyle} variant="subtitle2">
        {label}
      </Typography>
      {list.length === 0 ? (
        <Typography sx={textStyleWithMargin} variant="body2">
          -
        </Typography>
      ) : (
        <Grid container direction="column">
          {list.map((item) => (
            <div key={item.label}>
              <Grid container direction="row">
                {item.icon}
                <Grid sx={{ marginLeft: '10px' }}>
                  <Typography sx={textStyle} variant="body2">
                    {item.label}:
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                sx={{ marginBottom: 1, paddingLeft: '34px' }}
              >
                <Typography sx={valueStyle} variant="body2">
                  {item.value}
                </Typography>
              </Grid>
            </div>
          ))}
        </Grid>
      )}
    </>
  );
};
